import { useState, useCallback, type FormEvent } from 'react';
import { useClerk, useSignUp } from '@clerk/react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { Eye, EyeOff, ArrowRight, Loader2 } from 'lucide-react';
import { GoogleIcon } from './google-icon.js';
import { cn } from '../../lib/cn.js';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TFn = (key: string, defaultValue?: any) => string;

function errorMessage(clerkError: unknown, fallback: string, t: TFn): string {
  if (
    clerkError &&
    typeof clerkError === 'object' &&
    'errors' in clerkError &&
    Array.isArray((clerkError as { errors: unknown[] }).errors)
  ) {
    const first = (clerkError as { errors: Array<{ longMessage?: string; message?: string }> })
      .errors[0];
    if (first?.longMessage) return first.longMessage;
    if (first?.message) return first.message;
  }
  return t('auth.signup.errors.generic', fallback);
}

interface Props {
  /** Email pre-validated by the disposable-email precheck step */
  seedEmail: string;
}

type Step = 'credentials' | 'verify';

export function SignUpForm({ seedEmail }: Props) {
  const { t } = useTranslation('common');
  const { signUp, fetchStatus } = useSignUp();
  const clerk = useClerk();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>('credentials');
  const [email, setEmail] = useState(seedEmail);
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [googleInFlight, setGoogleInFlight] = useState(false);

  const loading = fetchStatus === 'fetching';

  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      setError('');
      if (!signUp) return;
      if (!email || !password) {
        setError(t('auth.signup.errors.missingFields', 'Please enter an email and password.'));
        return;
      }
      if (password.length < 8) {
        setError(t('auth.signup.errors.weakPassword', 'Password must be at least 8 characters.'));
        return;
      }

      // Re-run the disposable-email / rate-limit precheck against the CURRENT
      // email value. The parent page's precheck only gates the form mount; a
      // user can still edit the email in this form before submitting, which
      // would bypass the abuse controls without this server-side re-check.
      if (email !== seedEmail) {
        try {
          const baseUrl = import.meta.env.VITE_API_BASE || '/api';
          const res = await fetch(`${baseUrl}/v1/auth/signup-check`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email }),
          });
          if (res.status === 403) {
            setError(
              t(
                'auth.signup.disposableEmailError',
                'Please use a permanent email address. Disposable email services are not supported.',
              ),
            );
            return;
          }
          if (res.status === 429) {
            setError(
              t(
                'auth.signup.ipLimitError',
                'Too many accounts created from this network. Please try again tomorrow.',
              ),
            );
            return;
          }
        } catch {
          // Fail-open: if the precheck endpoint is unreachable, let Clerk's
          // server-side abuse controls take over and proceed with sign-up.
        }
      }

      // Single-shot: provide email + password; Clerk creates the user in one call
      const { error: createError } = await signUp.password({
        emailAddress: email,
        password,
      });
      if (createError) {
        setError(errorMessage(createError, 'Unable to create account.', t));
        return;
      }

      const { error: prepError } = await signUp.verifications.sendEmailCode();
      if (prepError) {
        setError(errorMessage(prepError, 'Unable to send verification email.', t));
        return;
      }
      setStep('verify');
    },
    [signUp, email, password, seedEmail, t],
  );

  const handleVerify = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      setError('');
      if (!signUp) return;
      if (!code) {
        setError(t('auth.signup.errors.missingCode', 'Please enter the code from your email.'));
        return;
      }
      const { error: verifyError } = await signUp.verifications.verifyEmailCode({ code });
      if (verifyError) {
        setError(errorMessage(verifyError, 'That code was not recognised.', t));
        return;
      }
      if (signUp.status === 'complete') {
        const { error: finalizeError } = await signUp.finalize();
        if (finalizeError) {
          setError(errorMessage(finalizeError, 'Unable to finalize sign up.', t));
          return;
        }
        navigate('/getting-started');
      }
    },
    [signUp, code, t, navigate],
  );

  const handleGoogle = useCallback(async () => {
    if (googleInFlight) return;
    setError('');
    // Use the stable signUp resource off the Clerk client (see
    // sign-in-form.tsx for the full rationale on stable vs future API).
    const stableSignUp = clerk.client?.signUp;
    if (!stableSignUp) {
      console.warn('[auth] Google sign-up clicked before Clerk SDK ready');
      setError(
        t(
          'auth.signup.errors.notReady',
          'Sign-up is still initializing. Please try again in a moment.',
        ),
      );
      return;
    }
    setGoogleInFlight(true);
    const watchdog = window.setTimeout(() => {
      setGoogleInFlight(false);
      setError(
        t('auth.signup.errors.stalled', "Google didn't open. Wait a moment, then try again."),
      );
    }, 6000);
    try {
      await stableSignUp.authenticateWithRedirect({
        strategy: 'oauth_google',
        redirectUrl: `${window.location.origin}/sso-callback`,
        redirectUrlComplete: `${window.location.origin}/getting-started`,
      });
    } catch (err) {
      console.error('[auth] signUp.authenticateWithRedirect threw', err);
      const code = (err as { errors?: Array<{ code?: string }> }).errors?.[0]?.code;
      const isRateLimited =
        code === 'rate_limit_exceeded' ||
        code === 'too_many_requests' ||
        (err instanceof Error && /429|rate.?limit/i.test(err.message));
      setError(
        isRateLimited
          ? t(
              'auth.signup.errors.rateLimited',
              'Too many sign-up attempts. Wait a minute and try again.',
            )
          : errorMessage(err, 'Google sign up failed. Check your connection and try again.', t),
      );
    } finally {
      window.clearTimeout(watchdog);
      setGoogleInFlight(false);
    }
  }, [clerk, t, googleInFlight]);

  if (step === 'verify') {
    return (
      <form onSubmit={handleVerify} className="flex flex-col gap-4" data-testid="signup-verify">
        <p className="text-small text-secondary">
          {t('auth.signup.verifyHint', 'We sent a 6-digit code to {{email}}. Enter it to finish.', {
            email,
          })}
        </p>
        <FieldLabel htmlFor="signup-code">
          {t('auth.signup.codeLabel', 'Verification code')}
        </FieldLabel>
        <GlassInput
          id="signup-code"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          required
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="123456"
        />
        <ErrorAlert message={error} />
        <PrimaryButton type="submit" loading={loading}>
          {t('auth.signup.verify', 'Verify email')}
        </PrimaryButton>
        <TextLink
          onClick={() => {
            setStep('credentials');
            setError('');
            setCode('');
          }}
        >
          {t('auth.signup.backToCredentials', 'Back')}
        </TextLink>
      </form>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <button
        type="button"
        onClick={handleGoogle}
        disabled={loading || googleInFlight}
        className="inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/[0.03] px-4 text-small font-medium text-primary transition-colors hover:border-white/20 hover:bg-white/[0.06] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400 disabled:opacity-60"
      >
        {googleInFlight ? (
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        ) : (
          <GoogleIcon className="h-4 w-4" />
        )}
        {t('auth.signup.google', 'Continue with Google')}
      </button>

      <Divider label={t('auth.signin.or', 'Or continue with')} />

      <form onSubmit={handleSubmit} className="flex flex-col gap-4" data-testid="signup-form">
        <div className="flex flex-col gap-1.5">
          <FieldLabel htmlFor="signup-email-input">
            {t('auth.signup.emailLabel', 'Email address')}
          </FieldLabel>
          <GlassInput
            id="signup-email-input"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('auth.signup.emailPlaceholder', 'you@example.com')}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <FieldLabel htmlFor="signup-password">
            {t('auth.signup.passwordLabel', 'Password')}
          </FieldLabel>
          <div className="relative">
            <GlassInput
              id="signup-password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="pr-11"
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={
                showPassword
                  ? t('auth.signin.hidePassword', 'Hide password')
                  : t('auth.signin.showPassword', 'Show password')
              }
              aria-pressed={showPassword}
              className="absolute inset-y-0 right-3 flex items-center rounded text-secondary transition-colors hover:text-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400"
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          <p className="text-caption text-muted">
            {t('auth.signup.passwordHint', 'At least 8 characters.')}
          </p>
        </div>

        <ErrorAlert message={error} />

        <PrimaryButton type="submit" loading={loading}>
          {t('auth.signup.submit', 'Create account')}
        </PrimaryButton>
      </form>
    </div>
  );
}

// ─── Shared primitives (mirror sign-in-form for consistency) ────────────────

function FieldLabel({ htmlFor, children }: { htmlFor: string; children: React.ReactNode }) {
  return (
    <label htmlFor={htmlFor} className="text-small font-medium text-secondary">
      {children}
    </label>
  );
}

function GlassInput({ className, ...props }: React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...props}
      className={cn(
        'w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-2.5 text-small text-primary placeholder:text-muted transition-colors',
        'focus:border-accent-lime/70 focus:bg-accent-lime/[0.06] focus:outline-none',
        className,
      )}
    />
  );
}

function PrimaryButton({
  loading,
  children,
  ...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { loading?: boolean }) {
  return (
    <button
      {...rest}
      disabled={loading || rest.disabled}
      className={cn(
        'inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-accent-lime px-4 text-small font-semibold text-canvas transition-[filter,transform] duration-150',
        'hover:brightness-105 active:scale-[0.98]',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400',
        'disabled:opacity-60',
        rest.className,
      )}
    >
      {loading ? (
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
      ) : (
        <>
          {children}
          <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </>
      )}
    </button>
  );
}

function Divider({ label }: { label: string }) {
  return (
    <div className="relative flex items-center">
      <span className="h-px flex-1 bg-white/[0.08]" />
      <span className="px-3 font-mono text-caption uppercase tracking-[0.15em] text-muted">
        {label}
      </span>
      <span className="h-px flex-1 bg-white/[0.08]" />
    </div>
  );
}

function ErrorAlert({ message }: { message: string }) {
  if (!message) return null;
  return (
    <p
      role="alert"
      data-testid="auth-form-error"
      className="rounded-lg border border-accent-red/40 bg-accent-red/[0.08] px-3 py-2 text-caption text-primary"
    >
      {message}
    </p>
  );
}

function TextLink({
  onClick,
  className,
  children,
}: {
  onClick: () => void;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'w-fit text-small text-accent-cyan transition-colors hover:underline',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400',
        className,
      )}
    >
      {children}
    </button>
  );
}
